import React, { useEffect, useState } from 'react'
import { Badge, Box, Button, ButtonGroup, Card, CardContent, Checkbox, Chip, FormControlLabel, LinearProgress, MenuItem, Select, Stack, TextField, Tooltip, Typography } from '@mui/material'
import { ClassNameMap, withStyles } from '@mui/styles'
import { toast } from 'react-toastify'
import Helper from '../../../../utils/helper'
import stylesheet from '../detail-container.stylesheet'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import InsertCommentIcon from '@mui/icons-material/InsertComment'
import { TaskService } from '../../../tasks/tasks.service'
import { PatientService } from '../../../../services/patient.service'
import { RPMActivityType } from '../../../../model/rpm.types'
import TaskDelayComponent from './task-delay.component'
import { DatePicker } from '@mui/x-date-pickers'
import moment from 'moment'
import { useSelector, useDispatch } from 'react-redux'
import { updateSingleDiagnosticTest } from '../../../../redux/patient-detail-slice'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import TaskHistoryComponent from '../../../tasks/task-history.component'
import ConfirmationDialogComponent from '../common/confirmation-dialog.component'
import { EditOutlined } from '@mui/icons-material'
import UploadTestResultComponent from '../diagnosis/upload-test-result.component'
import { RootState } from '../../../../redux/store'
import { formatDisplayName, stripPrefixAnywhere } from '../../../../utils/display-name-formatter'

interface IProps {
  input: any
  updateTaskData: any
  closeTaskAction: any
  classes: ClassNameMap
}

// Map task action types to RPM activity types
const mapTaskToRPMActivity = (actionType: string): RPMActivityType => {
  if (actionType === 'Call') return 'call'
  return 'care_coordination'
}

// Estimate duration based on task type (in minutes)
const estimateTaskDuration = (actionType: string): number => {
  if (actionType === 'Call') return 15
  return 10
}

const TaskActionComponent: React.FC<IProps> = ({ input, updateTaskData, closeTaskAction, classes }) => {
  const [taskLoading, setTaskLoading] = useState(false) as any
  const [isTaskAction, setIsTaskAction] = useState(null) as any
  const [isDelete, setIsDelete] = useState(false) as any
  const [testEdit, setTestEdit] = useState(null) as any
  const taskService = new TaskService()
  const patientService = new PatientService()
  const [isTaskComplete, setIsTaskComplete] = useState(input.currTask.task_status === 'Completed' ? true : false) as any
  const [taskAtmtsData, setTaskAtmtpsData] = useState({}) as any
  const [delayTrailData, setDelayTrailData] = useState({}) as any
  const [ownerList, setOwnerList] = useState(input.users || []) as any
  const [note, setNote] = useState('')
  const [scheduleDate, setScheduleDate] = useState() as any
  const [latestTask, setLatestTask] = useState({}) as any
  const [assignment, setAssignment] = useState({}) as any
  const [dateError, setDateError] = useState('')

  const patientDetail: any = useSelector((state: RootState) => state.patientDetail.patientDetail)
  const dispatch = useDispatch()

  // Auto-track RPM time when task is completed for RPM-enrolled patients
  const autoTrackRPMTime = async (taskTitle: string, actionType: string) => {
    const patientId = input.currTask?.patient_id || patientDetail?.id
    if (!patientId) return

    try {
      // Check if patient has active RPM enrollment
      const enrollRes = await patientService.getRPMEnrollments(patientId)
      if (!enrollRes?.data?.success) return

      const activeEnrollment = (enrollRes.data.rpm_enrollments || []).find((e: any) => e.status === 'active')

      if (!activeEnrollment) return

      // Create auto-tracked time entry
      const today = new Date()
      const activityDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`

      await patientService.addRPMTimeEntry(patientId, {
        rpm_enrollment_id: activeEnrollment.id,
        activity_date: activityDate,
        duration_minutes: estimateTaskDuration(actionType),
        activity_type: mapTaskToRPMActivity(actionType),
        notes: `Auto-tracked: ${taskTitle}`,
        is_auto_tracked: true,
      })

      console.log('RPM time auto-tracked for task:', taskTitle)
    } catch (err) {
      // Silently fail - auto-tracking should not interrupt task completion
      console.error('Failed to auto-track RPM time:', err)
    }
  }

  useEffect(() => {
    setOwnerList(input?.users || [])
    if (input.currTask.id) {
      setTaskAtmtpsData(input?.currTask?.attempts || {})
      setDelayTrailData(input?.currTask?.delays || {})
      setAssignment(input?.currTask?.assignment || {})
      setLatestTask({
        id: input.currTask.id,
        owner_uuid: input.currTask.owner_uuid,
        tracking_type: input.currTask.tracking_type,
        title: stripPrefixAnywhere(input.currTask.title) || input.currTask.title || '',
        action_type: input.currTask.action_type,
        due_date: input.currTask.due_date,
        owner_role: input.currTask.owner_role,
        note: input.currTask.note,
        delay_justification: '',
        log_attempt: '',
      })
      setNote(input.currTask.note)

      if (input.currTask?.due_date) setScheduleDate(input.currTask.due_date)
    }
  }, [input.currTask])
  const cancelTaskAction = () => {
    setIsTaskAction(null)
  }

  const handleErr = (err: any) => {
    setTaskLoading(false)
    cancelTaskAction()
    closeTaskAction()
    toast.error(Helper.getError(err), Helper.bottom_center())
  }

  const updateCurrTask = (res: any) => {
    setTaskLoading(false)
    cancelTaskAction()
    if (res?.data?.task) updateTaskData(res?.data?.task)
    closeTaskAction()
  }

  const isDisabled = () => {
    return taskLoading || (isTaskAction === 'delay_attempt' && latestTask?.delay_justification === '') || (isTaskAction === 'schedule' && dateError !== '')
  }

  const handleTaskDelete = () => {
    setIsDelete(true)
    updateTaskData({ action: 'delete' })
  }

  const handleTaskEdit = (ref_id: string) => setTestEdit(ref_id)

  const handleConfirmDeleteClose = () => {
    setIsDelete(false)
    updateTaskData({ action: 'delete_cancel' })
  }

  const confirmDeleteTask = () => {
    setTaskLoading(true)
    input.currTask?.id &&
      taskService
        .deleteTask(input.currTask?.id)
        .then((res: any) => {
          setTaskLoading(false)
          setIsDelete(false)
          if (res?.data?.message === 'Task deleted successfully') {
            updateTaskData('')
          }
        })
        .catch((error: any) => {
          setTaskLoading(false)
          setIsDelete(false)
          toast.error(Helper.getError(error), Helper.bottom_center())
        })
  }

  const validateDate = (d: any) => {
    const currDate = d ? moment(d).format('YYYY-MM-DD') : ''
    if (currDate === '') {
      setScheduleDate('')
      setDateError('')
    } else if (currDate === 'Invalid date') setDateError('Please select valid date')
    else if (currDate < moment().format('YYYY-MM-DD')) setDateError('Date must be on or after today')
    else {
      setScheduleDate(moment(d).format('YYYY-MM-DD'))
      setDateError('')
    }
  }

  const onSubmit = async () => {
    setTaskLoading(true)

    if (isTaskAction === 'log_attempt') {
      const log_payload = { note: latestTask.log_attempt || 'Attempted', is_updated_today: true }
      await taskService
        .postTaskLogAttempts(input.currTask.id, log_payload)
        .then((res: any) => {
          updateCurrTask(res)
        })
        .catch((err: any) => handleErr(err))
    }

    const updatedObj = {} as any
    Object.keys(latestTask).forEach((key) => {
      if (latestTask[key] !== input.currTask[key]) {
        updatedObj[key] = latestTask[key]
      }
    })

    let payload = {} as any
    for (const [key, value] of Object.entries(updatedObj)) {
      if (value !== undefined && value !== '' && value !== null) {
        payload[key] = value
      }
    }

    if (payload.owner_uuid === input.currTask.owner_uuid || payload.owner_uuid === '' || !payload.owner_uuid) {
      delete payload.owner_uuid
      delete payload.owner_role
    } else {
      payload.owner_role = latestTask.owner_role
    }

    if (isTaskAction === 'schedule') {
      if (scheduleDate === '') {
        payload = {
          ...payload,
          due_date: '',
        }
      } else if (scheduleDate && scheduleDate !== input.currTask?.due_date) {
        payload = {
          ...payload,
          due_date: moment(scheduleDate).format('YYYY-MM-DD'),
          task_status: 'SCHEDULED_FOR_FUTURE',
        }
      }
    } else if (isTaskAction === 'todo') {
      payload = {
        ...payload,
        due_date: '',
        task_status: 'To Do',
      }
    } else if (isTaskAction === 'task_status') {
      payload = {
        ...payload,
        task_status: isTaskComplete ? 'COMPLETED' : 'To Do',
      }
    }
    delete payload.log_attempt
    if (isTaskAction !== 'delay_attempt') delete payload.delay_justification
    if (isTaskAction !== 'schedule' && isTaskAction !== 'todo') delete payload.due_date
    if (isTaskAction === '') delete payload.task_status

    if (input.currTask.note !== note && note !== '') payload.note = note
    else delete payload.note

    const finalPayload = { ...payload, is_updated_today: true }
    taskService
      .putTaskSchedule(input.currTask.id, finalPayload)
      .then((res: any) => {
        // Auto-track RPM time when task is marked complete
        if (isTaskAction === 'task_status' && isTaskComplete) {
          autoTrackRPMTime(latestTask.title || input.currTask.title, input.currTask.action_type || 'Other')
        }
        updateCurrTask(res)
      })
      .catch((err: any) => handleErr(err))
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNote(event.target.value)
  }

  const updateLog = (action: any) => {
    if (action !== 'task_status') setIsTaskComplete(false)

    if (action !== 'schedule') {
      setScheduleDate('')
      setDateError('')
    }

    setIsTaskAction(action)
  }

  const updateTask = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const key = e.target.name === 'schedule' ? 'due_date' : ([e.target.name] as any)

    if (input.currTask.id && e.target.name === 'owner_uuid' && e.target.value !== '') {
      const selectedUserId = e.target.value
      const selectedUser = input?.users?.find((user: any) => user.id === selectedUserId)
      setLatestTask({
        ...latestTask,
        owner_uuid: selectedUserId,
        owner_role: selectedUser?.role ? Helper.toCamelCase(selectedUser.role) : '',
      })
    } else {
      setLatestTask({
        ...latestTask,
        [key]: e.target.name === 'action_type' ? (e.target.checked ? 'Call' : 'Other') : e.target.value,
      })
    }
  }

  const renderComment = (name: any, label: string, note_text: any, isDisabled: boolean) => {
    return (
      <Box pb={0.5} width={'100%'}>
        <TextField
          name={name}
          variant="outlined"
          placeholder={label}
          disabled={!isDisabled || taskLoading}
          size="small"
          value={note_text}
          inputProps={{ style: { fontSize: '0.7rem' } }}
          InputLabelProps={{ style: { fontSize: '0.7rem' } }}
          className={classes.fullWidth}
          multiline={true}
          sx={{
            '&. MuiInputBase-root-MuiOutlinedInput-root': {
              padding: '0.2rem !important',
            },
            '& .MuiOutlinedInput-root': {
              padding: '0.2rem 0rem 0.2rem 0.5rem !important',
            },
          }}
          rows="6"
          onChange={updateTask}
        />
      </Box>
    )
  }

  const getTooltipData = (info: any, count: any, label: any) => {
    const title = (
      <Card className={classes.taskDetailsTooltip}>
        <TaskDelayComponent input={{ [label === 'Log attempt' ? 'attempts' : 'delays']: info }} />
      </Card>
    )
    const cProps = {
      tooltip: {
        sx: {
          width: '20rem',
          backgroundColor: 'var(--color-body-background) !important',
          maxWidth: '35rem !important',
          padding: '0px !important',
        },
      },
    }
    return (
      <Tooltip title={title} placement="bottom" componentsProps={cProps}>
        {count}
      </Tooltip>
    )
  }

  const getStyleByStatus = () => {
    return input.currTask?.task_status === 'Completed'
      ? {
          cursor: input.currTask?.task_status === 'Completed' ? 'default !important' : 'inherit',
          color: 'grey !important',
        }
      : {}
  }

  const isStatusActive = (type: any) => {
    return {
      fill: input.currTask?.task_status === 'Completed' ? '#5e5e5e82' : isTaskAction === type ? 'var(--color-primary)' : 'grey',
      cursor: input.currTask?.task_status === 'Completed' ? 'default !important' : 'inherit',
      fontSize: '0.8rem !important',
    }
  }

  const getTestDetails = (ref_id: string) => {
    const prgObj = patientDetail?.diagnosis?.diagnostic_tests?.find((f: { id: any }) => f.id === ref_id) || ({} as any)
    return {
      title: prgObj.display_name || prgObj.name,
      id: prgObj.id,
      test_name: prgObj.name,
      test_notes: prgObj.test_notes || '',
      status: prgObj.current_status,
      site: prgObj?.site || {},
      mark_status: '',
      scheduled_date: prgObj?.scheduled_date || '',
      order_completed_date: prgObj?.order_completed_date || '',
      date_recorded: prgObj?.date_recorded || '',
      result_source: prgObj?.result_source || '',
      insurance_approval_status: prgObj?.insurance_approval_status || '',
      enrollment_id: (prgObj?.enrollment_ids || '').toString(),
    }
  }
  console.log(input)

  return (
    <Box mb={1} className={classes.customCard}>
      {taskLoading && <LinearProgress />}
      <Card
        variant="outlined"
        sx={{
          width: input.isTaskPanel ? 'inherit' : input?.isPatientTask ? '73%' : input.currTask?.test ? '95%' : '78%',
        }}
        onKeyUp={(e: any) => {
          e.preventDefault()
          if (e.key === 'Enter' && !['note', 'delay_justification', 'log_attempt'].includes(e?.target?.name) && !isDisabled()) onSubmit()
        }}
      >
        <CardContent sx={{ padding: '0.5rem 0.5rem 0.8rem 0.8rem' }}>
          <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
            <Typography variant="subtitle2">Update task</Typography>
            <Stack direction={'row'} spacing={0.5}>
              {input?.currTask?.ref_id && (
                <Tooltip title="Update test">
                  <EditOutlined className={`${classes.editIcon} ${classes.pointerCursor}`} onClick={() => handleTaskEdit(input.currTask.ref_id)} sx={{ fill: 'var(--color-primary)' }} />
                </Tooltip>
              )}
              {input.isTaskPanel && input?.currTask?.optional && (
                <Tooltip title="Delete task">
                  <DeleteOutlinedIcon className={`${classes.editIcon} ${classes.pointerCursor}`} onClick={() => handleTaskDelete()} sx={{ fill: 'var(--color-primary)' }} />
                </Tooltip>
              )}
            </Stack>
          </Stack>
          <Stack direction={'row'} spacing={1} alignItems={'flex-start'}>
            <Stack direction={'column'} spacing={1} sx={{ width: '40rem', minWidth: 0 }}>
              <Stack direction={'column'} spacing={1} pt={0.5} pr={1}>
                <Tooltip title={!input.currTask.is_adhoc ? 'Title is read-only for system generated task' : ''}>
                  <TextField
                    id="outlined-basic"
                    variant="standard"
                    autoFocus={input.currTask.is_adhoc}
                    InputProps={{ readOnly: !input.currTask.is_adhoc ? true : false }}
                    name="title"
                    size="small"
                    value={latestTask.title}
                    inputProps={{
                      style: { fontSize: '0.7rem' },
                    }}
                    InputLabelProps={{ style: { fontSize: '0.7rem' } }}
                    className={classes.fullWidth}
                    rows="2"
                    onChange={updateTask}
                  />
                </Tooltip>
                <TextField
                  name="note"
                  variant="outlined"
                  placeholder="Add note here"
                  disabled={taskLoading}
                  size="small"
                  value={note}
                  inputProps={{ style: { fontSize: '0.7rem' } }}
                  InputLabelProps={{ style: { fontSize: '0.7rem' } }}
                  className={classes.fullWidth}
                  multiline={true}
                  rows={input?.isTestTask ? 2 : 6}
                  onChange={handleChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      padding: '0.2rem 0rem 0.2rem 0.5rem !important',
                    },
                  }}
                />
              </Stack>
              {((input.currTask?.due_date && input.currTask?.task_status.includes('Upcoming')) || assignment?.assigned_to || assignment?.assignment_date) && (
                <Box>
                  {input.currTask?.due_date && <Typography className={classes.desc}>Scheduled on: {moment(input.currTask.due_date).format('MMM DD, YYYY')}</Typography>}
                  {assignment?.assigned_to && <Typography className={classes.desc}>Assigned to: {assignment?.assigned_to}</Typography>}
                  {assignment?.assignment_date && <Typography className={classes.desc}>Assignment date: {moment(assignment.assignment_date).format('MMM DD, YYYY')}</Typography>}
                </Box>
              )}
              <Box>
                <Typography className={classes.taskInput}>Owner</Typography>
                <Select
                  name={`owner_uuid`}
                  variant="standard"
                  margin="dense"
                  label={'Owner'}
                  value={latestTask.owner_uuid || ''}
                  defaultValue={latestTask.owner_uuid || ''}
                  onChange={(e: any) => updateTask(e)}
                  inputProps={{ classes: { icon: classes.icon } }}
                  sx={{
                    width: '6rem',
                    paddingTop: '0rem !important',
                    '.MuiSvgIcon-root': { fontSize: '0.8rem' },
                    '&.MuiInput-root': { padding: '0rem !important' },
                  }}
                >
                  {ownerList?.map((item: any) => (
                    <MenuItem
                      key={item.id || item} // Make sure key is unique and properly set
                      className={classes.taskInput}
                      value={item?.id}
                      selected={item?.id === latestTask.owner_uuid}
                    >
                      {item?.name}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              <Stack sx={{ width: '100%', alignItems: 'baseline', flexWrap: 'wrap' }} direction={'column'} spacing={0}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      sx={{
                        padding: '0rem !important',
                        marginLeft: '-0.3rem',
                        transform: 'scale(0.55)',
                        '& .MuiSvgIcon-root': {
                          fill: !input.isTaskPanel && isTaskAction === 'task_status' ? 'var(--color-primary) !important' : '',
                        },
                      }}
                      name="task_status"
                      checked={isTaskComplete}
                      onChange={(e: any) => {
                        setIsTaskComplete(e.target.checked)
                        updateLog('task_status')
                      }}
                    />
                  }
                  label={
                    <Typography pt={0.15} pl={'0rem !important'} className={classes.toxicityEvalBoxVal}>
                      Complete{' '}
                    </Typography>
                  }
                />
                {input.currTask.action_type === 'Call' && (
                  <Stack direction={'row'} spacing={5}>
                    <Stack
                      direction={'row'}
                      alignItems={'center'}
                      pr={delayTrailData.delay_count > 0 ? 1.8 : 0}
                      onClick={() => (input.currTask?.task_status === 'Completed' ? null : updateLog('delay_attempt'))}
                    >
                      <InsertCommentIcon className={classes.patientTaskIcon} sx={isStatusActive('delay_attempt')} />
                      {delayTrailData.count > 0 ? (
                        <Badge badgeContent={getTooltipData(delayTrailData, delayTrailData.count, `Delay with comment`)} color="primary" className={classes.taskAttemptBadge} sx={getStyleByStatus()}>
                          <Typography sx={getStyleByStatus()} className={`${classes.toxicityEvalBoxVal} ${input.currTask?.task_status !== 'Completed' ? classes.taskCursor : ''}`}>
                            {`Delay with comment`}
                          </Typography>
                        </Badge>
                      ) : (
                        <Typography sx={getStyleByStatus()} className={`${classes.toxicityEvalBoxVal} ${input.currTask?.task_status !== 'Completed' ? classes.taskCursor : ''}`}>
                          {`Delay with comment`}
                        </Typography>
                      )}
                    </Stack>
                    {['Intentionally Delayed'].includes(input.currTask.task_status) && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            sx={{
                              padding: '0rem !important',
                              marginLeft: '-0.2rem',
                              transform: 'scale(0.6)',
                              '& .MuiSvgIcon-root': {
                                fill: !input.isTaskPanel && isTaskAction === 'todo' ? 'var(--color-primary) !important' : '',
                              },
                            }}
                            name="task_status"
                            checked={isTaskAction === 'todo'}
                            onChange={(e: any) => {
                              updateLog(e.target.checked ? 'todo' : '')
                            }}
                          />
                        }
                        label={
                          <Typography pt={0.15} pl={0.2} className={classes.toxicityEvalBoxVal}>
                            Undo delay status
                          </Typography>
                        }
                      />
                    )}
                  </Stack>
                )}
                {isTaskAction === 'delay_attempt' && renderComment('delay_justification', 'Add delay comment', latestTask.delay_justification, isTaskAction === 'delay_attempt')}
                <Stack direction={'row'} alignItems={'center'} onClick={() => (input.currTask?.task_status === 'Completed' ? null : updateLog('schedule'))}>
                  <CalendarMonthIcon className={classes.patientTaskIcon} sx={isStatusActive('schedule')} />
                  <Typography sx={getStyleByStatus()} className={`${classes.toxicityEvalBoxVal} ${input.currTask?.task_status !== 'Completed' ? classes.taskCursor : ''}`}>
                    {`Schedule in future`}
                  </Typography>
                </Stack>
                {isTaskAction === 'schedule' && !taskLoading && (
                  <Box pl={0.25} pb={0.5}>
                    <Typography pl={0.2} pt={0.2} className={classes.taskInput}>
                      Due date
                    </Typography>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <DatePicker
                        className={classes.taskSchedule}
                        name={'schedule'}
                        format={'MM/DD/YYYY'}
                        formatDensity="dense"
                        slotProps={{
                          textField: {
                            size: 'small',
                            error: false,
                          },
                          actionBar: {
                            actions: ['clear'],
                          },
                        }}
                        disablePast
                        value={moment(moment(scheduleDate, 'YYYY-MM-DD'), 'MM/DD/YYYY')}
                        onChange={(e: any) => validateDate(e)}
                      />
                    </LocalizationProvider>
                  </Box>
                )}
                {dateError && (
                  <Typography color={'red'} className={classes.desc}>
                    {dateError}
                  </Typography>
                )}
                {input.currTask.action_type === 'Call' && (
                  <Stack direction={'row'} alignItems={'center'} onClick={() => (input.currTask?.task_status === 'Completed' ? null : updateLog('log_attempt'))}>
                    <InsertCommentIcon className={classes.patientTaskIcon} sx={isStatusActive('log_attempt')} />
                    {taskAtmtsData.count > 0 ? (
                      <Badge badgeContent={getTooltipData(taskAtmtsData, taskAtmtsData.count, `Log attempt`)} color="primary" className={classes.taskAttemptBadge}>
                        <Typography sx={getStyleByStatus()} className={`${classes.toxicityEvalBoxVal} ${input.currTask?.task_status !== 'Completed' ? classes.taskCursor : ''}`}>
                          {`Log attempt`}
                        </Typography>
                      </Badge>
                    ) : (
                      <Typography sx={getStyleByStatus()} className={`${classes.toxicityEvalBoxVal} ${input.currTask?.task_status !== 'Completed' ? classes.taskCursor : ''}`}>
                        {`Log attempt`}
                      </Typography>
                    )}
                  </Stack>
                )}
                {isTaskAction === 'log_attempt' && renderComment('log_attempt', 'Add attempt comment', latestTask.log_attempt, isTaskAction === 'log_attempt')}
              </Stack>
              <ButtonGroup className={classes.questionFullButtonStyle} size="small" variant="text">
                <Button className={classes.twoButtonWidth} disabled={isDisabled()} onClick={() => onSubmit()}>
                  <strong>{`Submit`}</strong>
                </Button>
                <Button onClick={() => closeTaskAction()} className={classes.twoButtonWidth}>
                  <strong> {'Cancel'}</strong>
                </Button>
              </ButtonGroup>
            </Stack>
            {!input.isTaskPanel && (
              <Box sx={{ width: '40rem', paddingLeft: '1rem', marginTop: '-1.1rem !important' }}>
                {input.currTask?.test &&
                  (() => {
                    const test = input.currTask.test
                    const status = test.current_status || ''
                    const statusText = status.replace(/_/g, ' ').replace(/\b\w/g, (c: any) => c.toUpperCase())
                    const getChipColors = () => {
                      if (['results_available'].includes(status)) return { bg: '#edf7ee', color: '#3d7a45' }
                      if (['ordered'].includes(status)) return { bg: '#eaf3ff', color: '#3a6fa8' }
                      if (['order_preparation'].includes(status)) return { bg: '#eef1ff', color: '#4a5eb5' }
                      if (['order_follow_up'].includes(status)) return { bg: '#f3eeff', color: '#6b4bbf' }
                      if (['deferred'].includes(status)) return { bg: '#f2f2f2', color: '#6b6b6b' }
                      if (['recommended'].includes(status)) return { bg: '#fff8ec', color: '#b07020' }
                      return { bg: '#fef0f5', color: '#b05080' }
                    }
                    const chipColors = getChipColors()
                    return (
                      <Box sx={{ mb: 3 }}>
                        {/* Dependent Test Label */}
                        <Stack direction="row" alignItems="center" spacing={0.7} sx={{ mb: 0.6 }}>
                          <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: chipColors.color, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Dependent Test</Typography>
                          <Box sx={{ flex: 1, height: '1px', background: `${chipColors.color}30` }} />
                        </Stack>
                        {/* Card */}
                        <Box
                          sx={{
                            borderRadius: '12px',
                            background: chipColors.bg,
                            border: `1.5px solid ${chipColors.color}35`,
                            borderLeft: `4px solid ${chipColors.color}`,
                            padding: '0.5rem 0.8rem',
                            boxShadow: `0 2px 8px ${chipColors.color}14`,
                          }}
                        >
                          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={0.5}>
                            <Typography
                              variant="body2"
                              className={classes.desc}
                              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 1, fontWeight: 600, color: 'var(--color-text-primary)', fontSize: '0.72rem' }}
                            >
                              {test.display_name || test.name?.replace(/_/g, ' ')}
                            </Typography>
                            {statusText && (
                              <Chip
                                label={statusText}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.56rem',
                                  flexShrink: 0,
                                  borderRadius: '6px',
                                  backgroundColor: 'rgba(255,255,255,0.75)',
                                  color: chipColors.color,
                                  fontWeight: 700,
                                  border: `1.5px solid ${chipColors.color}55`,
                                  '& .MuiChip-label': { px: '7px' },
                                }}
                              />
                            )}
                          </Stack>
                          {(test.site?.name || test.scheduled_date || test.date_recorded) && (
                            <Stack spacing={1.2} direction="row" alignItems="center" sx={{ mt: 0.4 }}>
                              {test.site?.name && (
                                <Stack direction="row" alignItems="center" spacing={0.3}>
                                  <LocationOnIcon sx={{ fontSize: '0.7rem', color: chipColors.color, opacity: 0.85 }} />
                                  <Typography sx={{ fontSize: '0.62rem', color: chipColors.color, fontWeight: 500 }}>{test.site.name.charAt(0).toUpperCase() + test.site.name.slice(1)}</Typography>
                                </Stack>
                              )}
                              {(test.scheduled_date || test.date_recorded) && (
                                <Stack direction="row" alignItems="center" spacing={0.3}>
                                  <CalendarMonthIcon sx={{ fontSize: '0.7rem', color: chipColors.color, opacity: 0.75 }} />
                                  <Typography sx={{ fontSize: '0.61rem', color: chipColors.color, opacity: 0.75 }}>
                                    <strong>{moment.unix(test.scheduled_date || test.date_recorded).format('MM/DD/YY')}</strong>
                                  </Typography>
                                </Stack>
                              )}
                            </Stack>
                          )}
                        </Box>
                      </Box>
                    )
                  })()}
                <Box>
                  <TaskHistoryComponent
                    input={{
                      patientId: input.currTask.patient_id,
                      isTaskEdit: input.currTask.action === 'edit' ? true : false,
                      task_id: '', //input.currTask.id,
                    }}
                  />
                </Box>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>

      {isDelete && <ConfirmationDialogComponent open={true} content={`Are you sure you want to delete task?`} handleConfirmClose={handleConfirmDeleteClose} confirmStatus={confirmDeleteTask} />}
      {Boolean(testEdit) && <UploadTestResultComponent open={true} input={getTestDetails(testEdit)} handleDialogClose={() => setTestEdit(null)} />}
    </Box>
  )
}

export default withStyles(stylesheet)(TaskActionComponent)
